// ── User roles and the checks that gate on them ─────────────────────────────
//
// Before this file, `UserRole` was declared six times across the app - in
// App.tsx, Layout.tsx, AdminPage, AssemblyOrdersPage, lib/supabase.ts and
// types/database.ts - with six different sets of values, and nothing imported
// any of them. Three omitted 'management', which exists in the database enum
// and is checked by every cost-visibility test.
//
// The role checks were copied around the same way: eleven hand-written
// membership tests, in two different orderings of the same array.

/**
 * Every role the application can store.
 *
 * `technicians.role` is a `text` column, so any of these can be written. Note
 * that the database `user_role` ENUM holds only DB_USER_ROLES below - the rest
 * exist only in application code. See APP_ONLY_ROLES.
 */
export const USER_ROLES = [
  'super_admin',
  'admin',
  'manager',
  'management',
  'technician',
  'marketing',
  'retail',
  'retail_shop',
] as const;

export type UserRole = (typeof USER_ROLES)[number];

/**
 * The values in the database's `user_role` ENUM.
 *
 * get_user_role() casts technicians.role to this type. Until
 * 20260819220000_get_user_role_tolerates_unknown.sql it raised 22P02 on
 * anything outside this list rather than returning NULL, which turned a
 * permission decision into a database error for the roles below.
 */
export const DB_USER_ROLES = [
  'super_admin',
  'admin',
  'manager',
  'management',
  'technician',
] as const;

/** Roles the app offers that the database ENUM has never contained. */
export const APP_ONLY_ROLES = ['marketing', 'retail', 'retail_shop'] as const;

/** Roles come off a text column, so every check takes a loose input. */
type RoleInput = string | null | undefined;

const has = (roles: readonly string[], role: RoleInput): boolean =>
  !!role && roles.includes(role);

// ── Predicates ──────────────────────────────────────────────────────────────

export function isSuperAdmin(role: RoleInput): boolean {
  return role === 'super_admin';
}

/** Administrators only. Does NOT include managers. */
export function isAdmin(role: RoleInput): boolean {
  return has(['super_admin', 'admin'], role);
}

/**
 * Administrators plus the two management roles.
 *
 * Several pages called this `isAdmin` locally, which read as the check above
 * while granting more. The two are deliberately named apart here.
 */
export function isManagerOrAbove(role: RoleInput): boolean {
  return has(['super_admin', 'admin', 'manager', 'management'], role);
}

export function isTechnician(role: RoleInput): boolean {
  return role === 'technician';
}

/**
 * Cost visibility. Every page uses this.
 *
 * PAODetailPage and StockIOPage previously used `role !== 'technician'`, a
 * deny-list that would also have admitted marketing, retail and retail_shop.
 * That never mattered in practice - those routes admit only super_admin,
 * admin, manager and technician - but it left cost visibility resting on a
 * route guard elsewhere rather than on a rule stated here.
 */
export function canViewCosts(role: RoleInput): boolean {
  return isManagerOrAbove(role);
}

/**
 * The Video Playlists page.
 *
 * Open by role to administrators and marketing. A technician gets it only when
 * granted individually (technicians.can_manage_playlists, set on the Admin
 * page). The grant is honoured for technicians alone, so a flag left behind on
 * an account moved to another role does not carry the page with it.
 */
export function canAccessVideoPlaylists(role: RoleInput, playlistGrant: boolean): boolean {
  return has(['super_admin', 'admin', 'marketing'], role) || (isTechnician(role) && playlistGrant);
}
